import { FC, Fragment, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  CommonStyle,
  ControlledForm,
  ControlledTable,
  Filter2
} from "Components";
import { RouteUrls } from "Constant/RouteUrls";
import CommonFormatter from "Utils/Common.Formatter";
import { TableLayout } from "Layouts";
import {
  actionArr,
  FilterArr,
  FormDataObj,
  TableCol,
} from "./Data";
import { useNavigate } from "react-router-dom";
import CstmBtn from "Components/CstmBtn";
import { DropdownArrPropsType, SingleSelectValType } from "DataTypes/Form";
import ProblemApi from "Service/Problem.api";
import ProbFormatter from "Utils/Prob.Formatter";
import { FormatDropdownData } from "Utils/DataFormatter";
import PreApprovedPlanApi from "Service/PreApprovedPlan.api";
import PreApprovedPlanFormatter from "Utils/PreApprovedPlan.Formatter";
import { CheckNumber } from "Utils/common";
import { useDispatch, useSelector } from "react-redux";
import {
  setAlertData,
  setPageFilter,
  setPageLimit,
  startLoading,
  stopLoading
} from "Redux/Screen/Action";
import {
  ScreenInitialStateType,
  UserInitialStateType
} from "DataTypes/Redux";
import { PreApprovedPlanListType } from "DataTypes/PatientManagement.type";
import { ModalLayout } from "Layouts";
import { CommonButtonArray, DeleteButtonArray } from "Components/Modal/Data";
import { Filter2DataProps } from "DataTypes/Common";
import {
  CreateFilter2Payload,
  RedoxDataToFilter,
  TransformFilterDataRedox
} from "Utils/common";
import UI from "Constant/UI";

const MyPreApprovedPlans: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const { listPagination, listFilters }: ScreenInitialStateType = useSelector(
    (state: any) => state?.screen
  );
  const listObj = listFilters.find(el => el.id == "my-pre-approved-plan");
  const reduxLimit = (listPagination || []).find(
    el => el.id == "my-pre-approved-plan"
  )?.limit || 10;
  const [rows, setRows] = useState<PreApprovedPlanListType[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [filterData, setFilterData] = useState<Filter2DataProps[]>(() =>
    RedoxDataToFilter(
      JSON.parse(JSON.stringify(FilterArr())),
      listObj
    )
  );
  const [pageData, setPageData] = useState({
    ...UI.pageData,
    limit: reduxLimit
  });
  const [searchText, setSearchText] = useState("");
  const [problemSearch, setProblemSearch] = useState("");
  const [problemDropArr, setProblemDropArr] =
    useState<DropdownArrPropsType[]>([]);
  const [problemDropVal, setProblemDropVal] =
    useState<SingleSelectValType>(null);
  const [deletePlan, setDeletePlan] =
    useState<PreApprovedPlanListType | null>(null);
  const [selectedPlan, setSelectedPlan] =
    useState<PreApprovedPlanListType | null>(null);
  const tableHeight = `calc(100% - ${filterHeight}px)`;

  useEffect(() => {
    CommonFormatter.HandleNavArr({
      id: "myPreApprovedPlans",
      label: "My Pre-Approved Plan(s)",
      path: `/${RouteUrls.hs}/${RouteUrls.myPreApprovedPlans}`,
      level: 1,
      link: RouteUrls.myPreApprovedPlans,
      navType: "my-items",
    });
    handleProblemSearch();
  }, []);

  useEffect(() => {
    handleSetData(1, pageData.limit);
  }, [filterData, myInfo.id]);

  const handleProblemSearch: (
    searchVal?: string
  ) => void = async (searchVal) => {
    const newSearchVal = searchVal || "";
    const res = await ProblemApi.list({
      limit: 20,
      offset: 0,
      data: { problemName: newSearchVal },
    });
    let newProbDrop: DropdownArrPropsType[] = [];
    if (res?.success) {
      const formattedData = ProbFormatter.List(res?.data);
      newProbDrop = FormatDropdownData(formattedData, "id", "name", "id");
    }
    setProblemDropArr(newProbDrop);
  };

  const handleSetData: (
    currentPage: number,
    currentLimit: number
  ) => void = async (currentPage, currentLimit) => {
    setLoading(true);
    const newFilterPayload = CreateFilter2Payload(filterData);
    const payload = {
      ...(newFilterPayload || {}),
      createdBy: myInfo.id,
      practiceId: null,
    };
    const res = await PreApprovedPlanApi.PlanList({
      offset: (currentPage - 1) * currentLimit,
      limit: currentLimit,
      data: payload
    });
    let newPageData = { ...UI.pageData };
    let newRows: PreApprovedPlanListType[] = [];
    if (res?.success) {
      newRows = PreApprovedPlanFormatter.PlanList(res?.data);
      newPageData = res.criterion;
    }
    setRows(newRows);
    setPageData(newPageData);
    dispatch(setPageLimit({
      id: "my-pre-approved-plan",
      limit: newPageData.limit
    }));
    dispatch(setPageFilter({
      id: "my-pre-approved-plan",
      filterData: TransformFilterDataRedox(filterData)
    }));
    setLoading(false);
  };

  const handleEdit: (
    data: PreApprovedPlanListType | null
  ) => void = (data) => {
    if (data) {
      if (data.status == "APPROVED") {
        setSelectedPlan(data);
      } else {
        navigate(`./${data.planId}/${data.versionId}/${RouteUrls.plan}`);
      }
    }
  };

  const handleCreateNewVersion: (
    data: PreApprovedPlanListType | null
  ) => void = async (data) => {
    if (data) {
      const payload = {
        careplanId: data.planId,
        planVersionId: data.versionId,
        createdBy: myInfo.id
      }
      const res =
        await PreApprovedPlanApi.GeneratePlanVersion({ data: payload });
      if (res?.success) {
        const newVersionId = CheckNumber(res?.data?.planVersionId);
        navigate(`./${data.planId}/${newVersionId}/${RouteUrls.plan}`);
      }
      setSelectedPlan(null);
    }
  };

  const handleView: (data: PreApprovedPlanListType) => void = (data) => {
    navigate(`./${data.planId}/${data.versionId}/${RouteUrls.plan}`);
  };

  const handleDelete: (
    data: PreApprovedPlanListType
  ) => void = (data) => {
    setDeletePlan(data);
  };

  const handleDeleteConfirm: () => void = async () => {
    if (!deletePlan?.planId) return;
    const payload = {
      careplanId: deletePlan.planId,
      planVersionId: deletePlan.versionId
    }
    const res = await PreApprovedPlanApi.DeletePlan({ data: payload });
    if (res?.success) {
      handleSetData(pageData.page, pageData.limit);
    }
    setDeletePlan(null);
  };

  const handleEnableDisable: (
    data: PreApprovedPlanListType
  ) => void = async (data) => {
    const payload = {
      planId: data.planId,
      planVersionId: data.versionId
    }
    const res = await PreApprovedPlanApi.TogglePlanStatus({
      data: payload
    });
    if (res?.success) {
      dispatch(setAlertData(res?.alert));
      handleSetData(pageData.page, pageData.limit);
    }
  };

  const handleCreatePlan: () => void = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
    const res = await PreApprovedPlanApi.TemplateSearch(
      { id1: problemDropVal }
    );
    if (res.success) {
      const formattedData = PreApprovedPlanFormatter.TemplateSearch(res?.data);
      const payload = {
        practiceId: myInfo.practiceId,
        category: formattedData
      };
      const res2 = await PreApprovedPlanApi.CreatePlan(
        { id1: problemDropVal, data: payload }
      );
      if (res2.success) {
        const planId = CheckNumber(res2?.data?.id);
        const versionId = CheckNumber(res2?.data?.planVersionId);
        navigate(`./${planId}/${versionId}/${RouteUrls.plan}`);
      }
    }
    dispatch(stopLoading());
  };

  const SampleUIWithFilter: JSX.Element = (
    <Box display="flex" alignItems="end" width="100%">
      <Box flex={1} mr={1}>
        <ControlledForm.SingleApi
          {...FormDataObj}
          onClick={async (val) => {
            setProblemSearch(
              problemDropArr.find(
                findEl => findEl.value == val
              )?.label || ""
            );
            setProblemDropVal(val);
          }}
          onChangeApiSearch={true}
          inputChange={async (val) => {
            setProblemSearch(val);
            setProblemDropVal(null);
            handleProblemSearch(val);
          }}
          inputVal={problemSearch}
          dropArr={problemDropArr}
          dropVal={problemDropVal}
          apiSearch={true}
          iconSearch={true}
        />
      </Box>
      <CstmBtn
        label="Create Pre-Approved Plan"
        onClick={handleCreatePlan}
        disable={!problemDropVal}
      />
    </Box>
  );

  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => TableCol()}
      tableRow={rows}
      tableAction={(data: PreApprovedPlanListType) =>
        actionArr(
          () => handleView(data),
          () => handleEdit(data),
          () => handleDelete(data),
          () => handleEnableDisable(data),
          data,
          myInfo.roleId
        )
      }
      searchText={searchText}
    />
  );

  return (
    <Box
      height="91%"
      sx={{
        ...CommonStyle.sxWhiteBox,
        overflow: "auto",
        px: 2
      }}>
      <ModalLayout
        modwidth="30%"
        heading={"Warning"}
        content={
          <Typography textAlign="center">
            Are you sure you want to delete this draft ?
          </Typography>
        }
        onClose={() => setDeletePlan(null)}
        open={deletePlan ? true : false}
        buttonArr={DeleteButtonArray(handleDeleteConfirm, () => {
          setDeletePlan(null);
        })}
      />
      <ModalLayout
        modwidth="33%"
        heading={"Caution"}
        content={
          <Fragment>
            <Typography textAlign="center">
              {"You are about to edit a preapproved plan," +
                "  this will remain as a draft until approved."}
            </Typography>
            <Typography textAlign="center" mt={2}>
              <b>{"Are you sure you'd like to edit the plan ?"}</b>
            </Typography>
          </Fragment>
        }
        open={selectedPlan ? true : false}
        onClose={() => setSelectedPlan(null)}
        buttonArr={CommonButtonArray(
          () => handleCreateNewVersion(selectedPlan),
          () => setSelectedPlan(null),
          "Yes",
          "No"
        )}
      />
      <Filter2
        filterDataArr={filterData}
        handleFilterClick={(data) => setFilterData(data)}
        changeSearchText={(val) => setSearchText(val)}
        setFilterHeight={(val) => setFilterHeight(val)}
        SampleUIWithFilter={SampleUIWithFilter}
      />
      <TableLayout
        tblBody={TblBodyJSX}
        tblHead={ControlledTable.TblHead(TableCol())}
        loading={loading}
        height={tableHeight}
        Pagination={
          <ControlledTable.Pagination
            mainPage={pageData.page}
            limit={pageData.limit}
            total={pageData.total}
            handleClick={handleSetData}
            tableStyle={true}
          />
        }
      />
    </Box>
  );
};
export default MyPreApprovedPlans;
